import { Box, Flex } from "@chakra-ui/layout";
import { useDisclosure } from "@chakra-ui/react";
import { saveAs } from "file-saver";
import { Formik } from "formik";
import React, { useState } from "react";
import { PdfApi } from "../../api/PdfApi";
import useComments from "../../hooks/useComments";
import useGetFormTabs from "../../hooks/useGetFormTabs";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { FormSectionStatus } from "../../lib/models/form-section-status.model";
import { RentalQuoteDetail } from "../../lib/models/quote.model";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import {
  ExternalRentalFormSchema,
  RentalBorrowerInformation,
  RentalForm,
  RentalFormSchema,
  RentalPremierForm,
  RentalPricing,
  RentalPropertySummary,
} from "../../lib/models/rental/rental-form.model";
import {
  RentalPremierQuote,
  RentalQuote,
} from "../../lib/models/rental/rental.model";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import { FixAndFlipQuote } from "../../lib/models/short-term/short-term.model";
import { getFileName } from "../../lib/pdf-generator";
import { QuoteUtils } from "../../lib/quote-utils";
import { PdfGenerationActions } from "../../lib/types/PDFGenerationActions";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { QuoteFormProps } from "../../lib/types/QuoteFormProps";
import { QuoteType } from "../../lib/types/QuoteType";
import { QuotingType } from "../../state/quote-reducer";
import QuoteBoxBottom from "./QuoteBoxBottom";
import QuoteBoxTop from "./QuoteBoxTop";
import QuoteFormTabs from "./QuoteFormTabs";

function RentalQuoteForm({
  quote,
  submitHandler,
  saveHandler,
  deleteHandler,
  saveQuoteMetrics,
  calculateHandler,
  isCalculating,
}: QuoteFormProps) {
  const quoteContext = useQuote();
  const userContext = useUser();
  const { comments } = useComments();

  const [form] = useState<RentalForm | RentalPremierForm>(
    setInitialRentalForm(quote, quote?.quoteType)
  );
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const { state, dispatch } = quoteContext;
  const { quoteCalculation: calculation, metadata } = state;
  const { sfProfile, isEmployee } = userContext;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tabIndex, setTabIndex] = useState(0);
  const quoteTabs = useGetFormTabs();
  const [preferredLoanAmt, setPreferredLoanAmt] = useState<number>();

  const formExportHandler = (action: PdfGenerationActions, formValues: any) => {
    if (state.quoteType === "Rental") {
      Promise.resolve(QuoteUtils.RentalFormToQuote(formValues))
        .then((quote: RentalQuote) => {
          quote.quoteType = "Rental";
          savePdf(quote, quote.quoteType, action);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      const premierForm = state.quote
        ? new RentalPremierForm(state.quote)
        : new RentalPremierForm(null);
      const values = { ...premierForm, ...formValues };
      Promise.resolve(QuoteUtils.RentalFormToPremierQuote(values))
        .then((quote: RentalPremierQuote) => {
          quote.quoteType = "Premier";
          savePdf(quote, quote.quoteType, action);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const savePdf = (
    quote: RentalQuote | RentalPremierQuote,
    quoteType: QuoteType,
    action: PdfGenerationActions
  ) => {
    if (!quote.scenario) {
      quote.scenario = QuoteUtils.GenerateRentalQuoteScenarios();
    }

    setLoadingPdf(true);

    normalizeQuoteForType(quote).then((normalQuote) => {
      generatePdf(normalQuote, quoteType, action).then((res) => {
        if (res?.data) {
          const blob = new Blob([res.data], { type: "application/pdf" });
          saveAs(blob, `${getFileName(quote?.quoteName, action)}.pdf`);
        }
      })
    }) 
      .then(() => {})
      .catch((error) => {
        console.error(error);
      });

    Promise.resolve(saveQuoteMetrics("PDF"))
      .then(() => {
        setLoadingPdf(false);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const normalizeQuoteForType = async (formValues: any) => {
    let quote: RentalQuote | RentalPremierQuote;
    let hasScenarios: boolean = false;

    if (state.quoteType === "Rental") {
      const quoteFromState = state.quote as RentalQuote;
      const rental = await QuoteUtils.RentalFormToQuote(formValues);
      rental.quoteType = "Rental";
      quote = rental;

      if (quoteFromState) {
        hasScenarios = !!quoteFromState.scenario?.length;
      }
    } else {
      const quoteFromState = state.quote as RentalPremierQuote;
      const premierForm = state.quote
        ? new RentalPremierForm(state.quote)
        : new RentalPremierForm(null);
      const values = { ...premierForm, ...formValues };
      const premier = await QuoteUtils.RentalFormToPremierQuote(values);
      premier.quoteType = "Premier";
      quote = premier;

      if (quoteFromState) {
        hasScenarios = !!quoteFromState.scenario?.length;
      }
    }

    if (!hasScenarios) {
      quote.scenario = QuoteUtils.GenerateRentalQuoteScenarios();
    } else {
      quote.scenario = state.rentalQuoteScenarios;
    }

    return quote;
  };

  const formActionHandler = (action: QuoteFormActions, formValues: any) => {
    const sfValues = state.sfIntegration;
    Promise.resolve(normalizeQuoteForType({ ...formValues, ...sfValues }))
      .then((quote: RentalQuote | RentalPremierQuote) => {
        dispatch({
          type: "QUOTE_UPDATED",
          payload: {
            quote,
          },
        });
        if (action === "CALCULATE") {
          calculateHandler(quote);
        } else if (action === "SAVE") {
          saveHandler(quote);
        } else if (action === "SAVE_AS") {
          onOpen();
        } else if (action === "SUBMIT") {
          submitHandler(quote);
        } else if (action === "SUBMIT_FOR_REVIEW") {
          submitHandler(quote);
        } else if (action === "DELETE") {
          deleteHandler(quote);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSaveAs = (
    values: FixAndFlipForm | RentalForm | RentalPremierForm
  ) => {
    Promise.resolve(normalizeQuoteForType(values))
      .then((quote) => {
        saveHandler(quote, true);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const generatePdf = async (
    quote: QuotingType,
    quoteType: QuoteType | null,
    action: PdfGenerationActions
  ) => {
    const pdfQuote = new RentalQuoteDetail(
      quote as RentalQuote,
      calculation as RentalCalculation
    );
    try {
        return await PdfApi.getRentalPdf({
          userProfile: sfProfile,
          metadata,
          action,
          quote: pdfQuote,
          discussionArray: comments,
        });
      } catch (err) {
      console.error(err);
    }
  };

  return (
    <Formik
      initialValues={form}
      validateOnBlur={true}
      validateOnChange={false}
      validateOnMount={true}
      onSubmit={() => {}}
      validationSchema={
        isEmployee ? RentalFormSchema : ExternalRentalFormSchema
      }
      enableReinitialize={true}
    >
      <>
        <Flex>
          <Box
            id="form"
            as="form"
            position="relative"
            paddingBottom={14}
            flexShrink={2}
            flex={1}
            maxWidth="full"
            minWidth="0"
          >
            <QuoteBoxTop
              quoteName={form.quoteName}
              formActionHandler={formActionHandler}
              formExportHandler={formExportHandler}
              loadingPdf={loadingPdf}
              isCalculating={isCalculating}
              quoteTabs={quoteTabs}
              setTabIndex={setTabIndex}
              calculation={calculation}
            />

            <QuoteFormTabs
              isCalculating={isCalculating}
              quoteTabs={quoteTabs}
              setTabIndex={setTabIndex}
              tabIndex={tabIndex}
              formStatusSections={formStatusSections}
              calculation={calculation}
              isFnf={false}
              quote={quote}
              formActionHandler={formActionHandler}
              preferredLoanAmt={preferredLoanAmt}
              setPreferredLoanAmt={setPreferredLoanAmt}
            />
          </Box>

          <QuoteBoxBottom
            formActionHandler={formActionHandler}
            calculation={calculation}
            onSaveClick={handleSaveAs}
            isOpen={isOpen}
            onClose={onClose}
          />
        </Flex>
      </>
    </Formik>
  );
}

export default RentalQuoteForm;

const setInitialRentalForm = (
  quote: RentalQuote | FixAndFlipQuote | null,
  quoteType: QuoteType = "Rental"
) => {
  if (!quote) {
    return new RentalForm(null);
  }

  if (quoteType === "Rental") {
    return quote ? new RentalForm(quote) : new RentalForm(null);
  } else {
    return quote ? new RentalPremierForm(quote) : new RentalPremierForm(null);
  }
};

const formStatusSections: FormSectionStatus[] = [
  {
    label: "Borrower Information",
    id: "borrower-information",
    fields: Object.keys(new RentalBorrowerInformation()),
    status: "info",
    value: "borrowerInformation",
  },
  {
    label: "Property Summary",
    id: "property-summary",
    fields: Object.keys(new RentalPropertySummary()),
    status: "info",
    value: "propertySummary",
  },
  {
    label: "Pricing",
    id: "pricing",
    fields: Object.keys(new RentalPricing()),
    status: "info",
    value: "pricing",
  },
];
